// Pantalla admin de configuración del estudio — datos fiscales que aparecen
// en los PDFs (recibos profesor, modelo 111). Singleton en BD; si NIF está
// vacío los PDFs llevan banner clay "documento no válido fiscalmente".

import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, AlertCircle, CheckCircle2, Building2 } from 'lucide-react';

import { getConfigEstudio, putConfigEstudio } from '../../lib/adminApi';
import { ApiError } from '../../lib/api';

export default function AdminConfigEstudio() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ nombre_fiscal: '', nif: '', domicilio_fiscal: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [okMsg, setOkMsg] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await getConfigEstudio();
      setData(r);
      setForm({
        nombre_fiscal: r.nombre_fiscal || '',
        nif: r.nif || '',
        domicilio_fiscal: r.domicilio_fiscal || '',
      });
    } catch (e) { setError(e); } finally { setLoading(false); }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const dirty = data && (
    form.nombre_fiscal !== (data.nombre_fiscal || '') ||
    form.nif !== (data.nif || '') ||
    form.domicilio_fiscal !== (data.domicilio_fiscal || '')
  );

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError(null); setOkMsg(null);
    try {
      const r = await putConfigEstudio({
        nombre_fiscal: form.nombre_fiscal,
        nif: form.nif,
        domicilio_fiscal: form.domicilio_fiscal,
      });
      setData(r);
      setForm({
        nombre_fiscal: r.nombre_fiscal || '',
        nif: r.nif || '',
        domicilio_fiscal: r.domicilio_fiscal || '',
      });
      setOkMsg(t('admin.config_estudio.guardado_ok'));
      setTimeout(() => setOkMsg(null), 3500);
    } catch (e) {
      setError(e instanceof ApiError ? e : new Error(e.message || t('admin.error_generico')));
    } finally { setSaving(false); }
  };

  return (
    <div className="admin-mi-cuenta">
      <header className="admin-page-header">
        <h1><Building2 size={20} style={{ marginRight: 8, verticalAlign: 'middle' }} />{t('admin.config_estudio.titulo')}</h1>
        <p className="admin-card-sub">{t('admin.config_estudio.sub')}</p>
      </header>

      {loading && <div className="admin-loading"><Loader2 size={24} className="spin" /></div>}
      {error && <div className="admin-error"><AlertCircle size={20} /><span>{error.message}</span></div>}

      {!loading && data && (
        <form className="admin-card" onSubmit={submit}>
          <h2 className="admin-card-titulo">{t('admin.config_estudio.card_titulo')}</h2>

          {!form.nif && (
            <div className="modal-error-general" style={{ marginBottom: 16 }}>
              <AlertCircle size={16} />
              <span>{t('admin.config_estudio.warn_sin_nif')}</span>
            </div>
          )}

          <div className="admin-form-grid one-col">
            <div className="modal-campo">
              <label>{t('admin.config_estudio.f.nombre_fiscal')}</label>
              <input
                type="text"
                value={form.nombre_fiscal}
                onChange={(e) => setForm((f) => ({ ...f, nombre_fiscal: e.target.value }))}
                placeholder={t('admin.config_estudio.f.nombre_placeholder')}
                maxLength={200}
              />
              <small className="admin-hint">{t('admin.config_estudio.f.nombre_hint')}</small>
            </div>
            <div className="modal-campo">
              <label>{t('admin.config_estudio.f.nif')}</label>
              <input
                type="text"
                value={form.nif}
                onChange={(e) => setForm((f) => ({ ...f, nif: e.target.value }))}
                placeholder={t('admin.config_estudio.f.nif_placeholder')}
                maxLength={50}
              />
              <small className="admin-hint">{t('admin.config_estudio.f.nif_hint')}</small>
            </div>
            <div className="modal-campo">
              <label>{t('admin.config_estudio.f.domicilio_fiscal')}</label>
              <input
                type="text"
                value={form.domicilio_fiscal}
                onChange={(e) => setForm((f) => ({ ...f, domicilio_fiscal: e.target.value }))}
                placeholder={t('admin.config_estudio.f.domicilio_placeholder')}
                maxLength={300}
              />
              <small className="admin-hint">{t('admin.config_estudio.f.domicilio_hint')}</small>
            </div>
          </div>

          {okMsg && (
            <div className="admin-msg-ok" style={{ marginTop: 16 }}>
              <CheckCircle2 size={16} /> <span>{okMsg}</span>
            </div>
          )}

          <div className="modal-acciones" style={{ marginTop: 18 }}>
            <button type="submit" className="btn-primary" disabled={!dirty || saving}>
              {saving ? <Loader2 size={16} className="spin" /> : t('comun.guardar')}
            </button>
          </div>

          {data.actualizado_at && (
            <p style={{ fontSize: 11, color: 'var(--ink-muted)', fontStyle: 'italic', marginTop: 12 }}>
              {t('admin.config_estudio.ultima_act')}: {new Date(data.actualizado_at).toLocaleString()}
            </p>
          )}
        </form>
      )}
    </div>
  );
}
